import React, { useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import { Icon } from "react-icons-kit";
import { ic_arrow_back } from "react-icons-kit/md";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import MainWrapper from "../../MainWrapper";
import Title from "../../elements/Title";
import Loader from "../../layouts/Loader";
import {
  CUBICASA_UNSYNCED_LIST_REQUEST,
  CUBICASA_MARK_SYNCED_REQUEST,
  CUBICASA_SYNC_RESET,
} from "../../../_utils/constants/CubiCasa";
import {
  mapWebhookRow,
  buildCreatedByOptions,
  buildCreatedAtOptions,
  applyFilters,
  paginate,
} from "./cubicasaHelpers";
import CubiCasaOrdersList from "./CubiCasaOrdersList";
import CubiCasaOrderDetailModal from "./CubiCasaOrderDetailModal";

const PAGE_SIZE = 10;
const TAKE = 100;

const initialFilters = {
  propertyAddress: "",
  createdBy: "",
  createdAt: "",
};

const UnSyncedCubiCasaOrders = ({
  auth: { auth, isAuthenticated },
  cubicasa: { unSyncedList, isLoading, isSyncing, syncedResult, syncError },
  fetchUnSyncedList,
  markSynced,
  resetSync,
}) => {
  const navigate = useNavigate();

  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Redirect unauthenticated users, otherwise fetch the un-synced webhook rows.
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    if (auth[0]?.token) {
      fetchUnSyncedList(auth[0].token, TAKE);
    }
  }, [auth, isAuthenticated]);

  // Map raw webhook rows to flat, null-safe view models once per data change.
  const viewRows = useMemo(
    () => (Array.isArray(unSyncedList) ? unSyncedList.map(mapWebhookRow) : []),
    [unSyncedList]
  );

  const createdByOptions = useMemo(
    () => buildCreatedByOptions(viewRows),
    [viewRows]
  );
  const createdAtOptions = useMemo(
    () => buildCreatedAtOptions(viewRows),
    [viewRows]
  );

  const filteredRows = useMemo(
    () => applyFilters(viewRows, filters),
    [viewRows, filters]
  );

  const pagination = useMemo(
    () => paginate(filteredRows, page, PAGE_SIZE),
    [filteredRows, page]
  );

  // function called on any filter value change — reset to first page
  const handleFilterChange = (e) => {
    const { id, value } = e.target;
    setFilters((prev) => ({ ...prev, [id]: value }));
    setPage(1);
  };

  const handleReset = () => {
    setFilters(initialFilters);
    setPage(1);
  };

  const handlePageChange = (nextPage) => setPage(nextPage);

  const openDetail = (row) => {
    setSelectedRow(row);
    setShowModal(true);
  };

  const closeDetail = () => {
    setShowModal(false);
    setSelectedRow(null);
  };

  // Dispatch the mark-synced request with the payload built inside the modal.
  const handleSyncProperty = (payload) => {
    if (auth[0]?.token) {
      markSynced(auth[0].token, payload.logId, payload);
    }
  };

  // On a successful sync: notify, close the modal and refresh the list so the
  // now-synced row drops off. On failure: surface the API message.
  useEffect(() => {
    if (syncedResult && syncedResult.success) {
      toast.success("Property synced successfully");
      closeDetail();
      if (auth[0]?.token) fetchUnSyncedList(auth[0].token, TAKE);
      resetSync();
    }
  }, [syncedResult]);

  useEffect(() => {
    if (syncError) {
      const message =
        syncError?.data?.message || "Failed to sync property. Please try again.";
      toast.error(message);
      resetSync();
    }
  }, [syncError]);

  return (
    <MainWrapper>
      <div className="container">
        <div className="sub-header mt-5 mb-3">
          <Title>Un-Synced CubiCasa Orders</Title>
          <Link className="btn btn-primary me-1" to="/dashboard">
            <Icon icon={ic_arrow_back} />
            Back
          </Link>
        </div>
        <Loader loading={isLoading}>
          <CubiCasaOrdersList
            rows={pagination.pageRows}
            filters={filters}
            createdByOptions={createdByOptions}
            createdAtOptions={createdAtOptions}
            handleFilterChange={handleFilterChange}
            handleReset={handleReset}
            pagination={pagination}
            handlePageChange={handlePageChange}
            onRowClick={openDetail}
          />
        </Loader>
      </div>
      <CubiCasaOrderDetailModal
        show={showModal}
        onHide={closeDetail}
        row={selectedRow}
        token={auth[0]?.token}
        userId={auth[0]?.userId}
        onSyncProperty={handleSyncProperty}
        isSyncing={isSyncing}
      />
    </MainWrapper>
  );
};

const mapStateToProps = ({ auth, cubicasa }) => ({ auth, cubicasa });

const mapDispatchToProps = (dispatch) => ({
  fetchUnSyncedList: (token, take) =>
    dispatch({ type: CUBICASA_UNSYNCED_LIST_REQUEST, token, take }),
  markSynced: (token, logId, payload) =>
    dispatch({ type: CUBICASA_MARK_SYNCED_REQUEST, token, logId, payload }),
  resetSync: () => dispatch({ type: CUBICASA_SYNC_RESET }),
});

export default connect(mapStateToProps, mapDispatchToProps)(UnSyncedCubiCasaOrders);
